import type {LocaleStatus} from "@khan/i18n";

import {type LoginUserData} from "../shared/types.ts";

// The type of LoginUserData selected from the User type in our GraphQL API.
//
// This type can be converted to LoginUserData using the
// adaptGraphQLLoginUserData function below.
interface GraphQLLoginUserData {
    id: string | null | undefined;
    kaid: string | null | undefined;
    canAccessDistrictsHomepage: boolean | null | undefined;
    hasUnresolvedInvitations: boolean | null | undefined;
    isTeacher: boolean | null | undefined;
    preferredKaLocale:
        | {
              id: string | null | undefined;
              kaLocale: string | null | undefined;
              status:
                  | "BEGINNING"
                  | "LITE"
                  | "TEST"
                  | "LIVE"
                  | "ROCK_STAR"
                  | null
                  | undefined;
          }
        | null
        | undefined;
    age?: number | null | undefined;
    nickname?: string | null | undefined;
    userDistrictInfos?: Array<{
        id: string;
        district: {
            id: string;
            kaLocale: string;
        };
    }>;
    cleverId?: string | null | undefined;
}

// Converts GraphQLLoginUserData selected from the User type in our GraphQL
// API to the LoginUserData type. The fields on the User type have looser
// type constraints, so this explicit conversation is needed to produce the
// necessary LoginUserData type.
export const adaptGraphQLLoginUserData = (
    graphqlUserData: GraphQLLoginUserData | null | undefined,
): LoginUserData | null => {
    if (!graphqlUserData) {
        return null;
    }
    if (!graphqlUserData.kaid) {
        return null;
    }
    return {
        kaid: graphqlUserData.kaid,
        canAccessDistrictsHomepage:
            graphqlUserData.canAccessDistrictsHomepage ?? false,
        hasUnresolvedInvitations:
            graphqlUserData.hasUnresolvedInvitations ?? false,
        isTeacher: graphqlUserData.isTeacher ?? false,
        preferredKaLocale: adaptGraphQLPreferredKALocale(
            graphqlUserData.preferredKaLocale,
        ),
        age: graphqlUserData.age,
        nickname: graphqlUserData.nickname,
        userDistrictInfos: graphqlUserData.userDistrictInfos,
        cleverId: graphqlUserData.cleverId,
    };
};

const adaptGraphQLPreferredKALocale = (
    graphqlData: GraphQLLoginUserData["preferredKaLocale"],
): LoginUserData["preferredKaLocale"] => {
    if (graphqlData == null) {
        return graphqlData;
    }
    if (!graphqlData.id) {
        return null;
    }
    if (!graphqlData.kaLocale) {
        return null;
    }
    if (!graphqlData.status) {
        return null;
    }
    return {
        id: graphqlData.id,
        kaLocale: graphqlData.kaLocale,
        status: graphqlData.status as LocaleStatus,
    };
};
